import { Component, EventEmitter, OnDestroy, OnInit, Output, Input, AfterViewInit, ViewChild, ElementRef, inject } from '@angular/core';
import { BehaviorsModule } from '@ddv/behaviors';
import { MetadataService } from '@ddv/datasets';
import { DashboardFilter, WidgetIdNamedQueryId } from '@ddv/models';
import { ButtonComponent } from '@hs/ui-core-inputs';
import { ModalDirective, ModalOptions } from '@hs/ui-core-layout';
import { ChipComponent, IconComponent } from '@hs/ui-core-presentation';
import { Subscription } from 'rxjs';

import { DashboardFiltersService } from '../../../services/dashboard-filters.service';
import { FilterableAttributeComponent } from '../filterable-attribute/filterable-attribute.component';
import { FilterableValueComponent } from '../filterable-values/filterable-value.component';

@Component({
    selector: 'ddv-dashboard-filters-modal',
    templateUrl: 'dashboard-filters-modal.component.html',
    styleUrls: ['dashboard-filters-modal.component.scss'],
    imports: [
        BehaviorsModule,
        FilterableAttributeComponent,
        FilterableValueComponent,
        ButtonComponent,
        ModalDirective,
        ChipComponent,
        IconComponent,
    ],
})
export class DashboardFiltersModalComponent implements OnInit, AfterViewInit, OnDestroy {
    @Input() widgetId = 0;
    @Input() dashboardFilters: DashboardFilter[] = [];

    @Output() filtersApplied = new EventEmitter<DashboardFilter[]>();

    protected showAddFilters = false;
    protected metadataLoaded = false;
    protected noFilterableData = true;
    protected modalOptions: ModalOptions = {};
    protected newDashboardFilter: DashboardFilter | undefined;
    protected showEditFilter = false;
    protected filterUnderEdit: DashboardFilter | undefined;

    private readonly dashboardFiltersService = inject(DashboardFiltersService);
    private readonly metadataService = inject(MetadataService);
    private metadataSubscription: Subscription | undefined;
    @ViewChild('anchorForAddFilterModal', { static: false, read: ElementRef }) private readonly addFiltersAnchor: ElementRef<HTMLElement> | undefined;

    // DashboardFiltersService is a singleton, as all services are by default
    // the implication is that many of these methods/state changes push to the server here will only ever happen
    // from one component at a time
    // additionally, these for method calls are only called by onInit, despite the fact that the arguments
    // come from @Input() properties.  I think we get away with this because this component is created/destroyed
    // and all these inputs are set when the component is created
    ngOnInit(): void {
        // this is actually used by both widget and coreWidget, but all that matters internally to the service
        // is whether it's dashboard.  so this should do for both cases
        this.dashboardFiltersService.setFilterType('widget');
        this.dashboardFiltersService.setSlicerValues(this.widgetId);
        this.dashboardFiltersService.setDashboardFilters([...this.dashboardFilters]);
        this.dashboardFiltersService.updateTotalValueCount();

        const widgetAndNamedQueryIdPairs = this.getRelevantWidgetAndNamedQueryIdPairs();
        this.noFilterableData = widgetAndNamedQueryIdPairs.length === 0;

        if (!this.noFilterableData) {
            this.fetchMetadataForFirstWidgetNamedQueryPair(widgetAndNamedQueryIdPairs[0]);
        }
    }

    ngAfterViewInit(): void {
        this.modalOptions = {
            anchorToElement: this.addFiltersAnchor?.nativeElement,
            position: 'bottom-right',
            customWidth: 55,
        };
    }

    ngOnDestroy(): void {
        this.metadataSubscription?.unsubscribe();
    }

    protected clearAllFilters(): void {
        this.dashboardFiltersService.clearDashboardFilters();
        this.refreshDashboardFilters();
    }

    protected onApplyFilters(): void {
        this.filtersApplied.emit(this.dashboardFiltersService.getDashboardFilters());
    }

    protected showAddFilter(): void {
        this.newDashboardFilter = undefined;
        this.showAddFilters = true;
    }

    protected addNewAttribute(selectedFilter: DashboardFilter): void {
        this.newDashboardFilter = selectedFilter;
    }

    protected addNewFilterEntry(selectedFilter: DashboardFilter): void {
        const dashboardFilters = this.dashboardFiltersService.getDashboardFilters();
        const dashboardFilter = dashboardFilters.some((filter) => filter.name === selectedFilter.name);
        if (!dashboardFilter) {
            this.dashboardFiltersService.addDashboardFilter(selectedFilter);
            this.refreshDashboardFilters();
        }
    }

    protected deleteFilterEntry(index: number): void {
        const dashboardFilters = this.dashboardFiltersService.getDashboardFilters();
        dashboardFilters?.splice(index, 1);
        this.dashboardFiltersService.setDashboardFilters(dashboardFilters);
        this.refreshDashboardFilters();
    }

    protected onEditFilter(filter: DashboardFilter): void {
        this.filterUnderEdit = filter;
        this.showEditFilter = true;
    }

    protected applyFilterEdit(editedFilter: DashboardFilter): void {
        const dashboardFilters = this.dashboardFiltersService.getDashboardFilters();
        const index = dashboardFilters.findIndex((f) => f.name === editedFilter.name);
        if (dashboardFilters[index]) {
            dashboardFilters[index] = editedFilter;
            this.dashboardFiltersService.setDashboardFilters(dashboardFilters);
        }
    }

    private getRelevantWidgetAndNamedQueryIdPairs(): WidgetIdNamedQueryId[] {
        if (this.widgetId) {
            return this.dashboardFiltersService.initFilterWidget(this.widgetId);
        }

        return [];
    }

    private fetchMetadataForFirstWidgetNamedQueryPair(context: WidgetIdNamedQueryId): void {
        this.metadataSubscription = this.metadataService.fetchUniqueMetadata(context)
            .subscribe({
                next: (uniqueMetadata) => {
                    if (!uniqueMetadata) {
                        return;
                    }

                    // this seems dangerous as its mutating data from the service
                    Object.entries(uniqueMetadata).forEach(([key, value]) => value.name = key);
                    this.metadataLoaded = true;
                    this.dashboardFiltersService.updateMetadata(uniqueMetadata);
                },
            });
    }

    private refreshDashboardFilters(): void {
        // this writes back to an input, which is an obvious no-no
        this.dashboardFilters = this.dashboardFiltersService.getDashboardFilters();
    }
}
